import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import useTestId from "../hooks/useTestId";
import { getRawData } from "../api/raw-data";
import React from "react";
import useSWR from "swr";

type Props = {
    isOpen: boolean;
    onCancel: () => void;
};
type TableData = Record<string, (number | null)[]>;
type RawData = Record<string, TableData>;

function RawDataModal({ isOpen, onCancel }: Props) {
    const testId = useTestId();

    const { data } = useSWR<RawData | null>(
        testId && isOpen ? `/raw-data/${testId}` : null,
        () => getRawData(testId)
    );
    if (!data) return null;

    const tableKeys = Object.keys(data);
    const dataKeys = Object.keys(data[tableKeys[0]]);

    const getTableArr = (tableKey: keyof typeof data) => {
        const tableData = data[tableKey];
        const arr = Array.from({ length: tableData[dataKeys[0]].length }).fill(
            null
        );
        return arr;
    };

    return (
        <Dialog open={isOpen} onClose={onCancel} className="relative z-50">
            <DialogBackdrop className="fixed inset-0 bg-black/70" />
            <div className="fixed inset-0 flex w-screen items-center justify-center p-3">
                <DialogPanel className="relative flex flex-col bg-blue-light h-full w-full border border-gray-medium shadow-lg pt-5 pb-0 px-2">
                    <button
                        className="absolute top-0 right-0 m-3 size-9 rounded-full hover:bg-gray-200"
                        onClick={onCancel}
                    >
                        <XMarkIcon className="size-full p-1.5" />
                    </button>
                    <div className="flex h-full w-full flex-col">
                        <h2 className="mb-4 font-bold text-[22px] text-center text-blue-dark">
                            Raw Data
                        </h2>
                        <div className="flex overflow-auto w-full">
                            <table className="mx-auto mb-10">
                                <thead className="sticky top-0 z-30 text-center bg-blue-dark text-white font-bold h-12 text-base">
                                    <tr className="">
                                        {dataKeys.map((item) => {
                                            const [paramenter, unit] =
                                                item.split(" ");
                                            return (
                                                <th
                                                    key={item}
                                                    className="px-2 leading-snug"
                                                >
                                                    {paramenter}{" "}
                                                    <span className="text-[15px] font-semibold">
                                                        {unit}
                                                    </span>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody className="">
                                    {tableKeys.map((tableKey, idx) => {
                                        const tableArray =
                                            getTableArr(tableKey);
                                        return (
                                            <React.Fragment key={idx}>
                                                <tr
                                                    className={
                                                        "sticky top-12 bg-blue-light shadow"
                                                    }
                                                    style={{
                                                        zIndex: 1 + idx,
                                                    }}
                                                >
                                                    <td
                                                        colSpan={
                                                            dataKeys.length
                                                        }
                                                        className="font-bold text-lg pt-3 pb-0.5"
                                                    >
                                                        {tableKey}
                                                    </td>
                                                </tr>
                                                {tableArray.map((_, index) => (
                                                    <tr
                                                        style={{
                                                            backgroundColor:
                                                                index % 2 === 0
                                                                    ? "white"
                                                                    : "#f6f6f6",
                                                            borderBottom:
                                                                index ===
                                                                tableArray.length -
                                                                    1
                                                                    ? "1px solid #ccc"
                                                                    : "none",
                                                        }}
                                                    >
                                                        {dataKeys.map((key) => (
                                                            <td
                                                                key={key}
                                                                className="text-center px-2 py-1.5"
                                                            >
                                                                {data[tableKey][
                                                                    key
                                                                ][
                                                                    index
                                                                ]?.toString() ||
                                                                    "null"}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}

export default RawDataModal;
